import React, { useEffect, useRef } from 'react';
import './CircuitryOverlay.css';

const CircuitryOverlay = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Circuit path definitions (fixed positions across viewport)
    const paths = [
      // Horizontal path - top
      { x1: 0.1, y1: 0.2, x2: 0.6, y2: 0.2, speed: 0.0003 },
      // Vertical path - right
      { x1: 0.7, y1: 0.15, x2: 0.7, y2: 0.7, speed: 0.0004 },
      // Diagonal path - center
      { x1: 0.3, y1: 0.4, x2: 0.65, y2: 0.6, speed: 0.00035 },
      // Horizontal path - bottom
      { x1: 0.15, y1: 0.75, x2: 0.5, y2: 0.75, speed: 0.0003 },
      // Vertical path - left
      { x1: 0.2, y1: 0.3, x2: 0.2, y2: 0.8, speed: 0.00038 },
      // Diagonal reverse
      { x1: 0.5, y1: 0.3, x2: 0.25, y2: 0.55, speed: 0.00042 },
      // Short horizontal - middle
      { x1: 0.4, y1: 0.5, x2: 0.75, y2: 0.5, speed: 0.00036 },
      // Short vertical - center
      { x1: 0.5, y1: 0.6, x2: 0.5, y2: 0.9, speed: 0.0004 }
    ];

    // Mobile performance: reduce paths
    const isMobile = window.innerWidth < 768;
    const activePaths = isMobile ? paths.slice(0, 3) : paths;

    // Energy pulses (one per path)
    const pulses = activePaths.map((path, i) => ({
      path: i,
      progress: Math.random(),
      speed: path.speed
    }));

    let lastTime = performance.now();

    const animate = (currentTime) => {
      const deltaTime = currentTime - lastTime;
      lastTime = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw circuit traces
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1.5;

      activePaths.forEach(path => {
        ctx.beginPath();
        ctx.moveTo(path.x1 * canvas.width, path.y1 * canvas.height);
        ctx.lineTo(path.x2 * canvas.width, path.y2 * canvas.height);
        ctx.stroke();
      });

      // Draw energy pulses
      pulses.forEach(pulse => {
        const path = activePaths[pulse.path];
        pulse.progress += pulse.speed * deltaTime;
        
        if (pulse.progress > 1) {
          pulse.progress = 0;
        }

        const x = path.x1 + (path.x2 - path.x1) * pulse.progress;
        const y = path.y1 + (path.y2 - path.y1) * pulse.progress;

        // Glow effect
        const gradient = ctx.createRadialGradient(
          x * canvas.width,
          y * canvas.height,
          0,
          x * canvas.width,
          y * canvas.height,
          12
        );
        gradient.addColorStop(0, 'rgba(0, 240, 255, 0.8)');
        gradient.addColorStop(0.5, 'rgba(0, 240, 255, 0.4)');
        gradient.addColorStop(1, 'rgba(0, 240, 255, 0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x * canvas.width, y * canvas.height, 12, 0, Math.PI * 2);
        ctx.fill();

        // Core dot
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.beginPath();
        ctx.arc(x * canvas.width, y * canvas.height, 3, 0, Math.PI * 2);
        ctx.fill();
      });

      requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="circuitry-overlay"
      data-testid="circuitry-overlay"
    />
  );
};

export default CircuitryOverlay;
